import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Filter, MoreHorizontal, Search } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { projectsApi } from "../lib/api";
import { SAMPLE_PROJECTS, PIPELINE_STAGES } from "../lib/sample-data";
import { cn } from "../lib/utils";

export default function Projects() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    projectsApi.list()
      .then((arr) => setItems(arr.length ? arr : SAMPLE_PROJECTS))
      .catch(() => setItems(SAMPLE_PROJECTS));
  }, []);

  const filtered = items.filter(
    (p) =>
      (filter === "all" || p.status === filter) &&
      (!q || p.title.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div data-testid="projects-page" className="space-y-8">
      <PageHeader
        kicker="Workspace"
        title="Projects"
        description="Every book, workbook, deck, flyer, campaign, and automation in one place."
        actions={
          <Link
            to="/create"
            data-testid="projects-create-btn"
            className="inline-flex items-center gap-2 px-4 h-11 rounded-xl pl-btn-aqua text-sm"
          >
            <Plus className="h-4 w-4" /> New Project
          </Link>
        }
      />

      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center gap-2 px-3 h-11 rounded-xl border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 flex-1">
          <Search className="h-4 w-4 text-[var(--pl-text-muted)]" />
          <input
            data-testid="projects-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search projects…"
            className="bg-transparent flex-1 text-sm text-[var(--pl-text)] outline-none placeholder:text-[var(--pl-text-muted)]"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap" data-testid="status-filter">
          <Pill active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all">
            All
          </Pill>
          {PIPELINE_STAGES.map((s) => (
            <Pill
              key={s.id}
              active={filter === s.id}
              onClick={() => setFilter(s.id)}
              testId={`filter-${s.id}`}
            >
              {s.label}
            </Pill>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map((p, i) => (
          <article
            key={p.id}
            data-testid={`project-${p.id}`}
            className="pl-fade-up rounded-2xl overflow-hidden border border-[var(--pl-border)] bg-[var(--pl-surface)]/70 backdrop-blur-md pl-lift"
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <div className="aspect-[16/10] relative overflow-hidden bg-[var(--pl-bg-2)]">
              {p.thumbnail ? (
                <img src={p.thumbnail} alt={p.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full pl-signature-gradient" />
              )}
              <div className="absolute top-3 left-3 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-[0.18em] bg-black/40 text-white border border-white/10 backdrop-blur">
                {p.type}
              </div>
              <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md text-[10px] capitalize bg-[var(--pl-aqua)]/20 border border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)] backdrop-blur">
                {p.status}
              </div>
            </div>
            <div className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium text-[var(--pl-text)] truncate">{p.title}</div>
                <button className="text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)]">
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </div>
              <div className="text-xs text-[var(--pl-text-muted)] line-clamp-2">
                {p.description}
              </div>
              <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full pl-signature-gradient" style={{ width: `${p.progress || 0}%` }} />
              </div>
              <div className="flex items-center justify-between text-[11px] text-[var(--pl-text-muted)]">
                <span>{p.progress || 0}% complete</span>
                <span className="font-mono">
                  {new Date(p.updated_at || Date.now()).toLocaleDateString()}
                </span>
              </div>
            </div>
          </article>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 text-[var(--pl-text-muted)] text-sm">
            No projects match. Start with{" "}
            <Link to="/create" className="text-[var(--pl-aqua)]">Create AI</Link>.
          </div>
        )}
      </div>
    </div>
  );
}

function Pill({ children, active, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "px-3 h-9 rounded-full text-xs capitalize border transition",
        active
          ? "bg-[var(--pl-aqua)]/15 text-[var(--pl-aqua)] border-[var(--pl-aqua)]/40"
          : "border-[var(--pl-border)] text-[var(--pl-text-muted)] hover:text-[var(--pl-text)]"
      )}
    >
      {children}
    </button>
  );
}
